'use client';

import Link from 'next/link';
import { IconArrowRight, IconBolt, IconCpu, IconDatabase } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { GPUProduct } from '@/lib/data/gpu-products';
import { formatHourlyRate, getUnavailableLabel } from '@/lib/data/gpu-products';

/**
 * Catalogue badges are merchandising, not status, so they stay on the
 * neutral/primary badge variants (status hues are reserved for machine and
 * payment states).
 */
const BADGE_VARIANT: Record<NonNullable<GPUProduct['badge']>, 'default' | 'secondary' | 'outline'> = {
  Popular: 'default',
  'Best Value': 'outline',
  New: 'secondary',
  Pro: 'outline',
};

interface GPUProductCardProps {
  gpu: GPUProduct;
  /**
   * Link into the create form, built with `machineCreateHref()`. Ignored for a
   * product that cannot be rented, which shows its status instead of a CTA.
   */
  rentHref?: string;
  /**
   * Hourly rate to show. Defaults to the catalogue base rate ("from"); the
   * products page passes the regional rate when a region filter is set.
   */
  price?: number;
  /** Caption above the price, e.g. "From" or the selected region. */
  priceLabel?: string;
  className?: string;
}

export function GPUProductCard({
  gpu,
  rentHref,
  price = gpu.basePrice,
  priceLabel = 'From',
  className,
}: GPUProductCardProps) {
  // Only a rentable product gets a link (and a price).
  const href = gpu.available ? rentHref : undefined;

  return (
    <div
      className={cn(
        'group relative flex flex-col h-full bg-card text-card-foreground rounded-2xl border border-border overflow-hidden',
        'transition-all duration-200',
        href && 'hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5',
        className
      )}
    >
      {/* Badge */}
      {gpu.available && gpu.badge && (
        <div className="absolute top-4 right-4 z-10">
          <Badge variant={BADGE_VARIANT[gpu.badge]} className="text-[10px] uppercase tracking-wider">
            {gpu.badge}
          </Badge>
        </div>
      )}

      {/* Header - fixed height so long names do not shift the grid */}
      <div className="p-5 pb-4 h-[108px]">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
          {gpu.series === 'datacenter' ? 'Datacenter GPU' : 'RTX Series'}
        </p>
        <h3 className="text-lg font-bold text-foreground mb-1 line-clamp-2 leading-tight pr-20">{gpu.name}</h3>
        <p className="text-sm text-muted-foreground truncate">{gpu.consumerEquivalent}</p>
      </div>

      {/* Specs */}
      <div className={cn('px-5 pb-4', !gpu.available && 'opacity-60')}>
        <div className="grid grid-cols-3 gap-3">
          <SpecTile icon={IconDatabase} value={`${gpu.vram}GB`} label="VRAM" />
          <SpecTile icon={IconCpu} value={String(gpu.vcpus)} label="vCPUs" />
          <SpecTile icon={IconBolt} value={`${gpu.ram}GB`} label="RAM" />
        </div>
      </div>

      {/* Use cases */}
      <div className="px-5 pb-4 min-h-[52px]">
        <div className="flex flex-wrap gap-1.5">
          {gpu.useCases.slice(0, 3).map((useCase) => (
            <span
              key={useCase}
              className="inline-flex px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground"
            >
              {useCase}
            </span>
          ))}
        </div>
      </div>

      <div className="flex-1" />

      {/* Price and CTA */}
      <div className="border-t border-border p-5">
        {href ? (
          <>
            <div className="mb-4 min-h-11">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5 truncate">
                {priceLabel}
              </p>
              <p className="text-2xl font-bold text-foreground tabular-nums">
                {formatHourlyRate(price)}
              </p>
            </div>
            <Button asChild className="w-full h-11 rounded-xl font-semibold">
              <Link href={href}>
                Rent now
                <IconArrowRight className="size-4" />
              </Link>
            </Button>
          </>
        ) : (
          // No price and no CTA: a product nobody can rent must not advertise
          // a rate or a button that leads nowhere.
          <div className="flex min-h-[100px] flex-col justify-center gap-2">
            <Badge variant="muted" className="text-[10px] uppercase tracking-wider">
              {getUnavailableLabel(gpu)}
            </Badge>
            <p className="text-sm text-muted-foreground">
              {gpu.withdrawn
                ? 'No longer offered for new machines.'
                : 'Not yet available to rent.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SpecTile({
  icon: Icon,
  value,
  label,
}: {
  icon: typeof IconCpu;
  value: string;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-[76px] bg-muted rounded-xl">
      <Icon className="size-4 text-primary mb-1.5" aria-hidden />
      <span className="text-sm font-semibold text-foreground tabular-nums">{value}</span>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</span>
    </div>
  );
}

// Compact variant for the featured section
interface GPUProductCardCompactProps {
  gpu: GPUProduct;
  /** Link into the create form; ignored when the product cannot be rented. */
  rentHref?: string;
  className?: string;
}

export function GPUProductCardCompact({ gpu, rentHref, className }: GPUProductCardCompactProps) {
  const href = gpu.available ? rentHref : undefined;

  const body = (
    <div className="flex flex-col h-full">
      {gpu.available && gpu.badge && (
        <Badge
          variant={BADGE_VARIANT[gpu.badge]}
          className="absolute top-3 right-3 text-[9px] uppercase tracking-wider px-2 py-0.5"
        >
          {gpu.badge}
        </Badge>
      )}
      <h4 className="text-sm font-bold text-foreground pr-20 mb-0.5 leading-tight">{gpu.name}</h4>
      <p className="text-[11px] text-muted-foreground mb-1">{gpu.consumerEquivalent}</p>
      <p className="text-[11px] text-muted-foreground tabular-nums">
        {gpu.vram}GB VRAM · {gpu.vcpus} vCPUs · {gpu.ram}GB RAM
      </p>

      <div className="flex-1 min-h-3" />

      <div className="flex items-center justify-between pt-3 border-t border-border">
        {href ? (
          <>
            <p className="text-base font-bold text-foreground tabular-nums">
              <span className="mr-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                From
              </span>
              {formatHourlyRate(gpu.basePrice)}
            </p>
            <IconArrowRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </>
        ) : (
          <span className="text-xs font-medium text-muted-foreground">{getUnavailableLabel(gpu)}</span>
        )}
      </div>
    </div>
  );

  const shell = cn(
    'group relative block w-full h-full text-left bg-card text-card-foreground rounded-xl border border-border p-4',
    'transition-all duration-200',
    href && 'hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5',
    className
  );

  return href ? (
    <Link href={href} className={shell}>
      {body}
    </Link>
  ) : (
    <div className={shell}>{body}</div>
  );
}
